import { useColors } from 'pdp/src/contexts/ColorsProvider/ColorsProvider'
import { useMemo } from 'react'

import { REDUCED_COLOR_LIST_MAX_LENGTH } from 'pdp/src/components/ColorsSelector/ColorsSelector'
import type { Product, ProductColor } from 'product/types'
import { ProductLocations } from 'product/types/ProductLocations'

import type { Color, FullProduct } from 'pdp/src//types/FullProduct'
import { useResponsiveBehavior } from 'responsive/hooks/useResponsiveBehavior/useResponsiveBehavior'

interface UseColorListParams {
	readonly product: FullProduct
	readonly location?: string
}

interface UseColorListResult {
	colorToRender: Color[]
	selectedColor: Color
	isColorListReduced: boolean
	parsedProduct: Product
}

export const useColorList = ({
	product,
	location,
}: UseColorListParams): UseColorListResult => {
	const { colors, selectedColor } = useColors()

	const parsedProduct = useMemo<Product>(
		() => ({
			...product,
			colors: product.colors.map(
				(color: Color): ProductColor => ({
					id: color.id,
					bulletImg: color.bulletImg,
					customizable: color.customizable,
					label: color.label,
					looks: {},
					sizes: color.sizes,
					relatedProductId: color.relatedProductId,
					relatedProductUrl: color.relatedProductUrl,
				})
			),
		}),
		[product]
	)

	const isColorListReduced = useMemo(
		() =>
			location === ProductLocations.ColorSelector &&
			colors.length > REDUCED_COLOR_LIST_MAX_LENGTH,
		[location, colors.length]
	)

	const colorToRender = useResponsiveBehavior<Color[]>({
		default: colors,
		lessThanLarge: isColorListReduced
			? colors.slice(0, REDUCED_COLOR_LIST_MAX_LENGTH - 1)
			: colors,
	})

	return { colorToRender, selectedColor, isColorListReduced, parsedProduct }
}
